import React, { useState } from "react"
import { Box } from "@shared-components/Box/Box"
import { AspectRatio } from "@shared-components/AspectRatio/AspectRatio"
import { GoogleMapMarkerWithInfoWindow } from "@shared-components/GoogleMap/GoogleMapMarkerWithInfoWindow"
import { Heading } from "@shared-components/Heading/Heading"
import { Text } from "@shared-components/Text/Text"
import { GoogleMap as SharedGoogleMap } from "@shared-components/GoogleMap/GoogleMap"
import mapStyle from "../../shared/components/GoogleMap/map-style.json"
import { MapModule } from "../../carlsberg-headless/types/modules/Map"
import {
  googleMapContainerStyles,
  googleMapStyles,
  infoContentStyles,
  infoHeadingStyles,
  infoLinkStyles,
  infoTextStyles,
} from "./styles"
import { useDictionaryContext } from "../../context/dictionary/useDictionaryContext"
import { useSiteContext } from "../../context/site/useSiteContext"
import { Nullable } from "../../shared/types/util"
import { DICTIONARY_MAP } from "../../carlsberg-headless/dictionary-map"
import { Container } from "@shared-components/Container/Container"
import { Link } from "@shared-components/Link/Link"
import { BaseModule } from "../../carlsberg-headless/types/modules/util/base-module"
import { TAXONOMIES_MAP } from "../../carlsberg-headless/taxonomies-map"
import { ThemeUIStyleObject } from "theme-ui"
import { Image } from "@shared-components/Image/Image"

export interface GoogleMapProps extends MapModule, BaseModule {
  cultureDirectionLinkName: Nullable<string>
  sx: ThemeUIStyleObject
}

const PureGoogleMap: React.FC<GoogleMapProps> = ({
  cbKey,
  zoom,
  center,
  markers,
  cultureDirectionLinkName,
  fallbackImage,
  taxonomies,
  sx,
}) => {
  const fallbackImageWidth = taxonomies.includes(
    TAXONOMIES_MAP.map_module.ratio_1_1
  )
    ? [472, 438, 620]
    : [454, 862, 1246]

  const [openIndex, setOpenIndex] = useState<string | number>("")

  if (!center || !markers) return null

  return (
    <Container sx={{ ...sx, ...googleMapStyles }} key={cbKey}>
      <Box sx={googleMapContainerStyles}>
        <AspectRatio
          ratio={[
            "355:600",
            taxonomies.includes(TAXONOMIES_MAP.map_module.ratio_1_1)
              ? "1:1"
              : "16:9",
          ]}
        >
          <SharedGoogleMap
            options={{ styles: mapStyle as any }}
            center={{ lat: center.lat ?? 0, lng: center.lng ?? 0 }}
            zoom={zoom ?? 13}
            fallbackImage={fallbackImage ?? undefined}
            fallbackImageWidth={fallbackImageWidth}
            onClick={() => setOpenIndex("")}
          >
            {markers &&
              markers.map(
                ({ body, directionUrl, key, position, title, image }) =>
                  position && (
                    <GoogleMapMarkerWithInfoWindow
                      key={key}
                      isOpen={key === openIndex}
                      position={{
                        lat: position?.lat ?? 0,
                        lng: position?.lng ?? 0,
                      }}
                      onOpenFnc={() => setOpenIndex(key)}
                    >
                      <Box sx={infoContentStyles}>
                        {image && (
                          <Image
                            src={`${image.fullUrl}?width=210&height=70&mode=crop`}
                            alt={image.altText ?? ""}
                            sx={{ mb: 4 }}
                          />
                        )}
                        {title && (
                          <Heading as="h6" sx={infoHeadingStyles}>
                            {title}
                          </Heading>
                        )}
                        {body && (
                          /* Change to shared text when merged with text module */
                          <Text
                            sx={infoTextStyles}
                            dangerouslySetInnerHTML={{ __html: body }}
                          />
                        )}
                        {directionUrl && (
                          <Link
                            to={directionUrl}
                            target="_blank"
                            sx={infoLinkStyles}
                          >
                            {cultureDirectionLinkName &&
                              cultureDirectionLinkName}
                          </Link>
                        )}
                      </Box>
                    </GoogleMapMarkerWithInfoWindow>
                  )
              )}
          </SharedGoogleMap>
        </AspectRatio>
      </Box>
    </Container>
  )
}

const GoogleMap: React.FC<GoogleMapProps> = props => {
  const { get } = useDictionaryContext()
  const { currentCulture } = useSiteContext()

  return (
    <PureGoogleMap
      cultureDirectionLinkName={get(
        DICTIONARY_MAP.map.map_direction_link,
        currentCulture
      )}
      {...props}
    />
  )
}

export { GoogleMap, PureGoogleMap }
